import React, { useState, useEffect } from 'react';
import { X, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import type { Ingrediente } from '../types/ingredientes';

interface ModalAjusteStockProps {
  isOpen: boolean;
  onClose: () => void;
  ingrediente: Ingrediente | null;
  onConfirm: (data: {
    stock_nuevo: number;
    motivo: string;
    notas?: string;
  }) => void;
  isLoading?: boolean;
}

const ModalAjusteStock: React.FC<ModalAjusteStockProps> = ({
  isOpen,
  onClose,
  ingrediente,
  onConfirm,
  isLoading = false,
}) => {
  const [stockNuevo, setStockNuevo] = useState<string>('');
  const [motivo, setMotivo] = useState<string>('');
  const [notas, setNotas] = useState<string>('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isOpen && ingrediente) {
      setStockNuevo(ingrediente.stockActual.toString());
      setMotivo('');
      setNotas('');
      setErrors({});
    }
  }, [isOpen, ingrediente]);

  if (!isOpen || !ingrediente) return null;

  const stockActual = ingrediente.stockActual;
  const stockNuevoNum = parseFloat(stockNuevo) || 0;
  const diferencia = stockNuevoNum - stockActual;
  const porcentajeCambio =
    stockActual > 0 ? ((diferencia / stockActual) * 100).toFixed(1) : '0';

  const getTipoMovimiento = () => {
    if (diferencia > 0) return 'entrada';
    if (diferencia < 0) return 'salida';
    return 'sin cambio';
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (stockNuevo === '' || isNaN(stockNuevoNum)) {
      newErrors.stockNuevo = 'El stock nuevo es requerido';
    } else if (stockNuevoNum < 0) {
      newErrors.stockNuevo = 'El stock no puede ser negativo';
    }

    if (!motivo.trim()) {
      newErrors.motivo = 'El motivo es requerido';
    } else if (motivo.trim().length < 3) {
      newErrors.motivo = 'El motivo debe tener al menos 3 caracteres';
    }

    if (diferencia === 0) {
      newErrors.stockNuevo =
        'El stock nuevo debe ser diferente al stock actual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      onConfirm({
        stock_nuevo: stockNuevoNum,
        motivo: motivo.trim(),
        notas: notas.trim() || undefined,
      });
    }
  };

  const motivosComunes = [
    'Inventario físico',
    'Corrección de error',
    'Diferencia de conteo',
    'Ajuste inicial',
    'Producto vencido',
    'Producto dañado',
    'Otro motivo',
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Ajuste Manual de Stock
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {ingrediente.nombre} -{' '}
              <span className="font-medium">
                Stock actual: {stockActual}{' '}
                {ingrediente.unidadMedida.abreviacion}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Stock Nuevo */}
          <div>
            <label
              htmlFor="stockNuevo"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Stock Nuevo *
            </label>
            <div className="relative">
              <input
                type="number"
                id="stockNuevo"
                value={stockNuevo}
                onChange={(e) => setStockNuevo(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.stockNuevo
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={`Ingrese el stock nuevo (${ingrediente.unidadMedida.abreviacion})`}
                step="0.01"
                min="0"
                disabled={isLoading}
              />
              <div className="absolute right-3 top-2 text-gray-500 dark:text-gray-400">
                {ingrediente.unidadMedida.abreviacion}
              </div>
            </div>
            {errors.stockNuevo && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle size={14} />
                {errors.stockNuevo}
              </p>
            )}

            {/* Indicador de cambio */}
            {!isNaN(stockNuevoNum) && diferencia !== 0 && (
              <div
                className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                  diferencia > 0
                    ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}
              >
                {diferencia > 0 ? (
                  <TrendingUp size={20} />
                ) : (
                  <TrendingDown size={20} />
                )}
                <div className="flex-1">
                  <div className="font-medium">
                    {getTipoMovimiento() === 'entrada'
                      ? 'Incremento'
                      : 'Decremento'}{' '}
                    de stock
                  </div>
                  <div className="text-sm">
                    {diferencia > 0 ? '+' : ''}
                    {diferencia.toFixed(2)}{' '}
                    {ingrediente.unidadMedida.abreviacion} ({porcentajeCambio}%)
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Motivo */}
          <div>
            <label
              htmlFor="motivo"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Motivo del Ajuste *
            </label>
            <select
              id="motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.motivo
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              disabled={isLoading}
            >
              <option value="">Seleccione un motivo</option>
              {motivosComunes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {errors.motivo && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle size={14} />
                {errors.motivo}
              </p>
            )}
          </div>

          {/* Notas opcionales */}
          <div>
            <label
              htmlFor="notas"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Notas Adicionales (Opcional)
            </label>
            <textarea
              id="notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Agregue detalles adicionales sobre este ajuste..."
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Máximo 500 caracteres
            </p>
          </div>

          {/* Alerta de Stock Crítico */}
          {stockNuevoNum > 0 && stockNuevoNum <= ingrediente.stockMinimo && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg flex items-start gap-3">
              <AlertCircle
                size={20}
                className="text-yellow-600 dark:text-yellow-400 mt-0.5"
              />
              <div className="flex-1">
                <h4 className="font-medium text-yellow-900 dark:text-yellow-200">
                  Stock bajo nivel mínimo
                </h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  El stock nuevo ({stockNuevoNum}{' '}
                  {ingrediente.unidadMedida.abreviacion}) está por debajo del
                  nivel mínimo ({ingrediente.stockMinimo}{' '}
                  {ingrediente.unidadMedida.abreviacion}). Considere realizar
                  una orden de compra.
                </p>
              </div>
            </div>
          )}

          {/* Resumen */}
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-2">
            <h4 className="font-medium text-gray-900 dark:text-white">
              Resumen del Ajuste
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Stock Actual:
                </span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {stockActual} {ingrediente.unidadMedida.abreviacion}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Stock Nuevo:
                </span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {stockNuevoNum > 0 ? stockNuevoNum.toFixed(2) : '0.00'}{' '}
                  {ingrediente.unidadMedida.abreviacion}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Diferencia:
                </span>
                <span
                  className={`ml-2 font-medium ${
                    diferencia > 0
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {diferencia > 0 ? '+' : ''}
                  {diferencia.toFixed(2)} {ingrediente.unidadMedida.abreviacion}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Tipo:</span>
                <span className="ml-2 font-medium capitalize text-gray-900 dark:text-white">
                  {getTipoMovimiento()}
                </span>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Ajustando...' : 'Confirmar Ajuste'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalAjusteStock;
